import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AuthProvider from './contexts/AuthProvider';
import PrivateRoute from './components/PrivateRoute';
import Sidebar from './components/Sidebar';
import Login from './pages/Login';
import SSOCallback from './pages/SSOCallback';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import ProductResourceDetail from './pages/ProductResourceDetail';
import ResourceList from './pages/ResourceList';
import ResourceDetail from './pages/ResourceDetail';
import BulkTranslate from './pages/BulkTranslate';
import Queue from './pages/Queue';
import History from './pages/History';
import DailyRuns from './pages/DailyRuns';
import Settings from './pages/Settings';
import SyncActivity from './pages/SyncActivity';
import ProductMetafields from './pages/ProductMetafields';
import VariantMetafields from './pages/VariantMetafields';
import CollectionMetafields from './pages/CollectionMetafields';
import MetafieldDetail from './pages/MetafieldDetail';
import MetaobjectDefinitionsPage from './pages/MetaobjectDefinitionsPage';
import MetaobjectDetail from './pages/MetaobjectDetail';
import ThemeStrings from './pages/ThemeStrings';
import FilterLabels from './pages/FilterLabels';
import Menus from './pages/Menus';
import Collections from './pages/Collections';
import ContentResources from './pages/ContentResources';
import ImageAlts from './pages/ImageAlts';
import Inbox from './pages/Inbox';
import Leaderboard from './pages/Leaderboard';

// Apply saved theme globally (works on login page too)
function useThemeInit() {
  useEffect(() => {
    const stored = localStorage.getItem('theme');
    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    if (stored === 'dark' || (!stored && prefersDark)) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);
}

function AppLayout({ children }) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      <main className={`flex-1 ${collapsed ? 'ml-[68px]' : 'ml-56'} min-h-screen transition-all duration-200`}>
        {children}
      </main>
    </div>
  );
}

function ProtectedPage({ children }) {
  return (
    <PrivateRoute>
      <AppLayout>{children}</AppLayout>
    </PrivateRoute>
  );
}

function App() {
  useThemeInit();

  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/sso-callback" element={<SSOCallback />} />
          <Route path="/dashboard" element={<ProtectedPage><Dashboard /></ProtectedPage>} />
          <Route path="/products" element={<ProtectedPage><Products /></ProtectedPage>} />
          <Route path="/products/:id" element={<ProtectedPage><ProductDetail /></ProtectedPage>} />
          {/* Phase 21 D-10: drill-in for the new (Phase 20-backed) Products view.
              Pitfall 9: /products/:productId/resources is more specific than
              /products/:id — React Router v6 picks the more specific match. */}
          <Route path="/products/:productId/resources" element={<ProtectedPage><ProductResourceDetail /></ProtectedPage>} />
          <Route path="/collections" element={<ProtectedPage><Collections /></ProtectedPage>} />
          <Route path="/collections/:id" element={<ProtectedPage><ResourceDetail type="COLLECTION" /></ProtectedPage>} />
          <Route path="/articles" element={<ProtectedPage><ContentResources resourceType="ARTICLE" /></ProtectedPage>} />
          <Route path="/articles/:id" element={<ProtectedPage><ResourceDetail type="ARTICLE" /></ProtectedPage>} />
          <Route path="/pages-content" element={<ProtectedPage><ContentResources resourceType="PAGE" /></ProtectedPage>} />
          <Route path="/pages-content/:id" element={<ProtectedPage><ResourceDetail type="PAGE" /></ProtectedPage>} />
          <Route path="/menus" element={<ProtectedPage><Menus /></ProtectedPage>} />
          <Route path="/menus/:id" element={<ProtectedPage><ResourceDetail type="MENU" /></ProtectedPage>} />
          <Route path="/image-alts" element={<ProtectedPage><ImageAlts /></ProtectedPage>} />
          <Route path="/bulk" element={<ProtectedPage><BulkTranslate /></ProtectedPage>} />
          <Route path="/queue" element={<ProtectedPage><Queue /></ProtectedPage>} />
          <Route path="/history" element={<ProtectedPage><History /></ProtectedPage>} />
          <Route path="/daily-runs" element={<ProtectedPage><DailyRuns /></ProtectedPage>} />
          <Route path="/settings" element={<ProtectedPage><Settings /></ProtectedPage>} />
          <Route path="/sync-activity" element={<ProtectedPage><SyncActivity /></ProtectedPage>} />
          <Route path="/metafields/product" element={<ProtectedPage><ProductMetafields /></ProtectedPage>} />
          <Route path="/metafields/variant" element={<ProtectedPage><VariantMetafields /></ProtectedPage>} />
          <Route path="/metafields/collection" element={<ProtectedPage><CollectionMetafields /></ProtectedPage>} />
          <Route path="/metafields/:ownerType/:id" element={<ProtectedPage><MetafieldDetail /></ProtectedPage>} />
          <Route path="/metaobjects" element={<ProtectedPage><MetaobjectDefinitionsPage /></ProtectedPage>} />
          <Route path="/metaobjects/:id" element={<ProtectedPage><MetaobjectDetail /></ProtectedPage>} />
          <Route path="/theme-strings" element={<ProtectedPage><ThemeStrings /></ProtectedPage>} />
          <Route path="/filter-labels" element={<ProtectedPage><FilterLabels /></ProtectedPage>} />
          {/* Phase 22 — feedback split into Inbox + Leaderboard. Old route
              redirects so any saved bookmarks land in the right place. */}
          <Route path="/feedback/inbox" element={<ProtectedPage><Inbox /></ProtectedPage>} />
          <Route path="/feedback/leaderboard" element={<ProtectedPage><Leaderboard /></ProtectedPage>} />
          <Route path="/translation-feedback" element={<Navigate to="/feedback/inbox" replace />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
